import React, { useEffect, useState } from "react";
import {
  Button,
  Divider,
  Grid,
  Typography,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  Pagination,
} from "@mui/material";
import CustomTextField from "src/components/forms/theme-elements/CustomTextField";
import CustomFormLabel from "src/components/forms/theme-elements/CustomFormLabel";
import ParentCard from "src/components/shared/ParentCard";
import Breadcrumb from "src/layouts/full/shared/breadcrumb/Breadcrumb";
import { useFormik } from "formik";
import * as yup from "yup";
import { useNavigate } from "react-router";
import swal from "sweetalert";
import { IconArrowBack } from "@tabler/icons";
import MasterListService from "src/service/masterList";
import GeofencingService from "src/service/geofencing";

const BCrumb = [
  {
    to: "/",
    title: "Home",
  },
  {
    title: "Geofence Allotment to Vehicle",
  },
];

const validationSchema = yup.object({
  geofenc_id: yup.string().required("Geo fencing is required"),
  radius: yup.string().required("Geo fence radius is required"),
  geo_fence_margin: yup.string().required("Geo fence margin is required"),
});

const FencingAllotmenttoVehicle = () => {
  const sessionData = JSON.parse(localStorage.getItem("user"));
  const navigate = useNavigate();

  const [geofenc, setGeofenc] = useState([]);
  const [vehicleList, setVehicleList] = useState([]);
  const [filteredVehicles, setFilteredVehicles] = useState([]);
  const [districtList, setDistrictList] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(18);

  const fetchGeofenceList = async () => {
    try {
      const res = await MasterListService.detgeofenceList({});
      setGeofenc(res.data);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchDistrictList = async () => {
    try {
      const res = await GeofencingService.fetch_district_list({});
      setDistrictList([{ id: "", district_name: "All" }, ...res.data]);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchVehicleList = async (id) => {
    try {
      const res = await GeofencingService.fetch_vehicle_list({
        district_code: id,
      });
      setVehicleList(res.data);
      setFilteredVehicles(res.data); // Initially set all vehicles
    } catch (error) {
      console.log(error);
    }
  };

  const initialValues = {
    geofenc_id: "",
    radius: "",
    vehicle_no: [],
    geo_fence_margin: "",
    email_id: "",
    c_by: sessionData.data.id,
  };

  const { values, errors, touched, handleChange, handleBlur, handleSubmit } =
    useFormik({
      initialValues: initialValues,
      validationSchema: validationSchema,
      onSubmit: async (values) => {
        const { district_code, search, ...finalValues } = values;
        try {
          const res = await GeofencingService.save_multiple_vehicle(
            finalValues
          );
          swal("success", res.message, "success");
          // Reset form values
          values.geofenc_id = "";
          values.radius = "";
          values.geo_fence_margin = "";
          values.vehicle_no = [];
          setTimeout(() => {
            navigate("/geofencereport");
          }, 1000);
        } catch (error) {
          swal("error", error, "error");
        }
      },
    });

  useEffect(() => {
    fetchGeofenceList();
    fetchDistrictList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionData.data.id, sessionData.data.user_type]);

  // Handle district selection change
  const handleDistrictChange = async (event, newValue) => {
    handleChange({
      target: {
        name: "district_code",
        value: newValue ? newValue.id : "",
      },
    });
    try {
      await fetchVehicleList(newValue ? newValue.id : "");
    } catch (error) {
      console.error("Error fetching vehicles:", error);
    }
  };

  // Filter vehicles based on search query
  useEffect(() => {
    const filtered = vehicleList.filter((vehicle) =>
      vehicle.vehicle_no.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredVehicles(filtered);
  }, [searchQuery, vehicleList]);

  // Handle search input change
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    handleChange(event);
  };

  // Calculate the displayed vehicles based on the current page and items per page
  const indexOfLastVehicle = currentPage * itemsPerPage;
  const indexOfFirstVehicle = indexOfLastVehicle - itemsPerPage;
  const currentVehicles = filteredVehicles.slice(
    indexOfFirstVehicle,
    indexOfLastVehicle
  );

  // Handle pagination change
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  return (
    <div>
      <Breadcrumb title="Geofence Allotment to Vehicle" items={BCrumb} />
      <ParentCard title="">
        <Grid container alignItems="center">
          <Grid item xs={4}>
            <Typography
              variant="h2"
              sx={{
                fontSize: "22px",
                fontWeight: "bold",
                fontFamily: "Sans-Serif",
              }}
            >
              Geofence Allotment to Vehicle
            </Typography>
          </Grid>
          <Grid
            item
            xs={8}
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              marginBottom: "15px;",
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                navigate("/geofencereport");
              }}
            >
              <IconArrowBack width={20} />
            </Button>
          </Grid>
        </Grid>
        <Divider />
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3} style={{ marginTop: "10px" }}>
            <Grid item lg={4} md={4} sm={6} xs={12}>
              <CustomFormLabel htmlFor="geofenc_id">
                Geo Fence Name <span style={{ color: "red" }}>*</span>
              </CustomFormLabel>
              <Autocomplete
                id="geofenc_id"
                name="geofenc_id"
                options={geofenc}
                getOptionLabel={(option) => `${option.place_name}`}
                value={
                  geofenc.find((item) => item.id === values.geofenc_id) || null
                }
                onChange={(event, newValue) => {
                  handleChange({
                    target: { name: "geofenc_id", value: newValue?.id || "" },
                  });
                  handleChange({
                    target: { name: "radius", value: newValue?.radius || "" },
                  });
                }}
                onBlur={handleBlur}
                renderInput={(params) => (
                  <CustomTextField
                    {...params}
                    error={touched.geofenc_id && Boolean(errors.geofenc_id)}
                    helperText={touched.geofenc_id && errors.geofenc_id}
                    fullWidth
                    variant="outlined"
                    placeholder="Search or select Area"
                  />
                )}
              />
            </Grid>
            <Grid item lg={4} md={4} sm={6} xs={12}>
              <CustomFormLabel htmlFor="radius">
                Radius (In Km) <span style={{ color: "red" }}>*</span>
              </CustomFormLabel>
              <CustomTextField
                id="radius"
                name="radius"
                type="text"
                value={values.radius}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.radius && Boolean(errors.radius)}
                helperText={touched.radius && errors.radius}
                variant="outlined"
                placeholder="Enter Geo fence radius in Km"
                fullWidth
              />
            </Grid>
            <Grid item lg={4} md={4} sm={6} xs={12}>
              <CustomFormLabel htmlFor="geo_fence_margin">
                Extra Margin (In metre) <span style={{ color: "red" }}>*</span>
              </CustomFormLabel>
              <CustomTextField
                id="geo_fence_margin"
                name="geo_fence_margin"
                type="text"
                value={values.geo_fence_margin}
                onChange={handleChange}
                onBlur={handleBlur}
                error={
                  touched.geo_fence_margin && Boolean(errors.geo_fence_margin)
                }
                helperText={touched.geo_fence_margin && errors.geo_fence_margin}
                variant="outlined"
                placeholder="Enter Geo fence margin in metre"
                fullWidth
              />
            </Grid>
            <Grid item lg={4} md={4} sm={6} xs={12}>
              <CustomFormLabel htmlFor="radius">E-Mail Id</CustomFormLabel>
              <CustomTextField
                id="email_id"
                name="email_id"
                type="email"
                value={values.email_id}
                onChange={handleChange}
                variant="outlined"
                placeholder="Enter Email for alert"
                fullWidth
              />
            </Grid>
            <Grid item lg={4} md={4} sm={6} xs={12}>
              <CustomFormLabel htmlFor="district_code">
                District <span style={{ color: "red" }}>*</span>
              </CustomFormLabel>
              <Autocomplete
                id="district_code"
                name="district_code"
                options={districtList}
                getOptionLabel={(option) => option.district_name}
                onChange={handleDistrictChange} // Handle district change
                onBlur={handleBlur}
                renderInput={(params) => (
                  <CustomTextField
                    {...params}
                    error={
                      touched.district_code && Boolean(errors.district_code)
                    }
                    helperText={touched.district_code && errors.district_code}
                    fullWidth
                    variant="outlined"
                    placeholder="Search or select a District"
                  />
                )}
              />
            </Grid>
            <Grid item lg={4} md={4} sm={6} xs={12}>
              <CustomFormLabel htmlFor="radius">
                Search vehicle No <span style={{ color: "red" }}>*</span>
              </CustomFormLabel>
              <CustomTextField
                id="search"
                name="search"
                type="text"
                value={searchQuery} // Bind search query state
                onChange={handleSearchChange} // Handle search input change
                onBlur={handleBlur}
                error={touched.search && Boolean(errors.search)}
                helperText={touched.search && errors.search}
                variant="outlined"
                placeholder="Search vehicle No"
                fullWidth
              />
            </Grid>
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <CustomFormLabel htmlFor="vehicle_no">
                Vehicle Number <span style={{ color: "red" }}>*</span>
              </CustomFormLabel>

              <Grid container spacing={3} style={{ marginTop: "10px" }}>
                {currentVehicles.length > 0 ? (
                  currentVehicles.map((vehicle) => (
                    <Grid
                      item
                      lg={2}
                      md={3}
                      sm={4}
                      xs={6}
                      key={vehicle.vehicle_no}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.vehicle_no.includes(
                              vehicle.vehicle_no
                            )}
                            onChange={handleChange}
                            name="vehicle_no"
                            value={vehicle.vehicle_no}
                          />
                        }
                        label={`${vehicle.vehicle_no}`}
                      />
                    </Grid>
                  ))
                ) : (
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <h6 style={{ textAlign: "center", color: "red" }}>
                      No Vehicles Found !!
                    </h6>
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Pagination
                count={Math.ceil(filteredVehicles.length / itemsPerPage)}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Grid>
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Button color="success" variant="contained" type="submit">
                Submit
              </Button>
            </Grid>
          </Grid>
        </form>
      </ParentCard>
    </div>
  );
};

export default FencingAllotmenttoVehicle;
